import { useEffect, useRef } from "react";
import { useSocketIOContext } from "../../SocketIOContext";
import { useGameStore, useUserStore, WebSocketBattleState } from "../../../store";
import { IUserinfo } from "../../../types/interface";
import { MarkerPosition } from "./owlControls/useBattleOwlControls";
import { REACT_APP_BACKEND_URL } from "../../../config";
import {useTokenStore} from "../../../store/useTokenStore";

export const ROUND_DURATION_MS = 10000;
export const ANIMATIONS_GRACE_PERIOD_MS = 5000;

type AdditionalUserBattleInfoResponse = {
  username: string;
  avatar: string | null;
  skin: {
    img: string;
  } | null;
  max_hp: number;
  current_hp: number;
  happiness: number;
  level: number;
  health: number;
};

/**
 * Initiates matchmaking by emitting a "START_FIGHT" event.
 */
export const useStartOwlMatchmaking = () => {
  const { socket } = useSocketIOContext();

  const startMatchmaking = (
    userInfo: IUserinfo,
    difficulty?: "easy" | "medium" | "hard"
  ) => {
    if (!socket) return;
    socket.emit("START_FIGHT", {
      user_tgid: userInfo.tg_id,
      fight_type: difficulty ?? "easy",
    });
  };

  return startMatchmaking;
};

// Custom hook that returns a function to send a battle choice.
export const useMakeBattleChoice = () => {
  const { socket } = useSocketIOContext();

  const makeBattleChoice = (
    userInfo: IUserinfo,
    attack: MarkerPosition | null,
    defence: MarkerPosition | null
  ) => {
    if (!socket) return;
    socket.emit("FIGHT_CHOICE", {
      user_tgid: userInfo.tg_id,
      attack,
      defence,
    });
  };

  return makeBattleChoice;
};


/**
 * Hook to manage battle state updates. It listens to separate events:
 * - "FIGHT_UPDATE"
 * - "FIGHT_END"
 * - "WILL_START"
 *
 * Each handler updates battle state, fetches additional enemy info when needed,
 * and (if applicable) updates rewards.
 */
export const useOwlBattleService = () => {
  const { socket } = useSocketIOContext();
  const { accessToken } = useTokenStore();
  const {
    battleError,
    battleState,
    setBattleState,
    setBattleError,
    setIncomingBattleState,
    setBattleSpoils,
    setBattlePlayer1,
    setBattlePlayer2,
  } = useGameStore();
  const { user, setUser } = useUserStore();
  const useCurrentBattleAsSourceRef = useRef(true);

  const fetchAdditionalUsersInfo = (source: WebSocketBattleState) => {
    // If current battle info already exists, skip fetching.
    if (user?.current_battle) return;
    // Only fetch at the start of the battle (i.e. round 0).
    if (source.round !== 0) return;


    // Clear existing opponent info.
    setBattlePlayer1(null);
    setBattlePlayer2(null);
    setBattlePlayer1({
      username: user.username || "Anonymous",
      maxHp: 20 + user.endurance * 3,
      skin: user.equipped?.filter((item: any) => item.good.type === 'skin'),
      avatar: user.avatar ?? null,
      happiness: user.happiness,
      level: user.level,
      health: user.health
    });

    fetch(`${REACT_APP_BACKEND_URL}/getEnemyInfo/${source.uuid}/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => res.json())
      .then((data: AdditionalUserBattleInfoResponse) => {
        setBattlePlayer2({
          username: data.username || "Anonymous",
          maxHp: data.max_hp,
          skin: data.skin ?? null,
          avatar: data.avatar ?? null,
          happiness: data.happiness,
          level: data.level,
          health: data.health
        });
      })
      .catch(() => {});
  };

  console.log('Battle State', battleState);
  // If the user already has a battle stored, update state immediately.
  useEffect(() => {
    if (!useCurrentBattleAsSourceRef.current || !user?.current_battle) return;
    console.log('FIX1', user.current_battle.player1.health);
    setBattleState(user.current_battle);
    setBattlePlayer1({
      username: user.current_battle.player1.username,
      maxHp: user.current_battle.player1.max_hp,
      skin: user.current_battle.player1.skin ?? null,
      avatar: user.current_battle.player1.avatar ?? null,
      happiness: user.current_battle.player1.happiness,
      health: user.current_battle.player1.health,
    });
    setBattlePlayer2({
      username: user.current_battle.player2.username,
      maxHp: user.current_battle.player2.max_hp,
      skin: user.current_battle.player2.skin ?? null,
      avatar: user.current_battle.player2.avatar ?? null,
      happiness: user.current_battle.player2.happiness,
      health: user.current_battle.player2.health,
    });
  }, [user?.current_battle, setBattleState, setBattlePlayer1, setBattlePlayer2]);

  useEffect(() => {
    if (!socket) return;

    // Handle "FIGHT_UPDATE" events.
    const fightUpdateHandler = (message: any) => {
      if (message.error) {
        setBattleState(null);
        setBattleSpoils(null);
        setBattleError(message.error);
        return;
      } else if (battleError) {
        setBattleError(null);
      }
      setBattleState(message.state);
      fetchAdditionalUsersInfo(message.state);

      if (message.state.rewards) {
        setBattleSpoils({
          exp: message.state.rewards.exp,
          coins: message.state.rewards.coins,
        });
        setUser({
          ...user,
          totalExp: user.totalExp + message.state.rewards.exp,
          coins: user.coins + message.state.rewards.coins,
        });
      }
    };

    // Handle "FIGHT_END" events.
    const fightEndHandler = (message: any) => {
      if (message.error) {
        setBattleState(null);
        setBattleSpoils(null);
        setBattleError(message.error);
        return;
      } else if (battleError) {
        setBattleError(null);
      }
      const retryTimer = setTimeout(() => {
        setBattleState(message.state);
        fetchAdditionalUsersInfo(message.state);
      }, ROUND_DURATION_MS);
      clearTimeout(retryTimer);

      if (message.state.rewards) {
        setBattleSpoils({
          exp: message.state.rewards.exp,
          coins: message.state.rewards.coins,
        });
        setUser({
          ...user,
          totalExp: user.totalExp + message.state.rewards.exp,
          coins: user.coins + message.state.rewards.coins,
        });
      }
    };

    // Handle "WILL_START" events.
    const willStartHandler = (message: any) => {
      setIncomingBattleState(message.state);
      fetchAdditionalUsersInfo(message.state);

      setTimeout(() => {
        setBattleState(message.state);
        setIncomingBattleState(null);
      }, ROUND_DURATION_MS);
    };

    // Subscribe to the separate events.
    socket.on("FIGHT_UPDATE", fightUpdateHandler);
    socket.on("FIGHT_END", fightEndHandler);
    socket.on("WILL_START", willStartHandler);

    // Clean up event listeners on unmount.
    return () => {
      socket.off("FIGHT_UPDATE", fightUpdateHandler);
      socket.off("FIGHT_END", fightEndHandler);
      socket.off("WILL_START", willStartHandler);
    };
  }, [
    socket,
    battleError,
    setBattleError,
    setBattleState,
    setBattleSpoils,
    setIncomingBattleState,
    setBattlePlayer1,
    setBattlePlayer2,
    setUser,
    user,
  ]);
};
